import { WorkoutFilter as WorkoutFilterDto, WorkoutOrderField } from '../service/core-service'

/** The multi-select filter categories that live in the workouts URL query (one repeated param each). */
export type WorkoutFilterAttributes = Pick<
  WorkoutFilterDto,
  'bodyParts' | 'equipment' | 'intensities' | 'trainerIds' | 'types' | 'durations' | 'styles' | 'sections'
>

export type WorkoutSort = {
  orderField: WorkoutOrderField
  orderDirection: 'ASC' | 'DESC'
}

/** Newest First — the default sort for the Results view and section pages (GH-754). */
export const DEFAULT_WORKOUT_SORT: WorkoutSort = { orderField: 'availableAt', orderDirection: 'DESC' }

/** The state encoded in / decoded from the workouts URL query string (the single source of truth). */
export interface WorkoutQueryState {
  workoutFilter: WorkoutFilterAttributes
  sort: WorkoutSort
  hideCompleted: boolean
  page: number
}

export const WORKOUT_FILTER_KEYS: (keyof WorkoutFilterAttributes)[] = [
  'bodyParts',
  'equipment',
  'intensities',
  'trainerIds',
  'types',
  'durations',
  'styles',
  'sections',
]

const SORT_PARAM = 'sort'
const HIDE_COMPLETED_PARAM = 'hideCompleted'
const PAGE_PARAM = 'page'

/** The Sort By tokens (GH-754), in display order. The single source for both the URL and the dropdown. */
export type WorkoutSortToken = 'newest' | 'popular' | 'title-asc' | 'title-desc'

// URL sort token ⇄ orderField/orderDirection. The Sort By dropdown derives its options from this map,
// so there is one source of truth — adding a token here is enough.
export const WORKOUT_SORT_BY_TOKEN: Record<WorkoutSortToken, WorkoutSort> = {
  newest: { orderField: 'availableAt', orderDirection: 'DESC' },
  popular: { orderField: 'popularity', orderDirection: 'DESC' },
  'title-asc': { orderField: 'title', orderDirection: 'ASC' },
  'title-desc': { orderField: 'title', orderDirection: 'DESC' },
}
export const WORKOUT_SORT_TOKENS = Object.keys(WORKOUT_SORT_BY_TOKEN) as WorkoutSortToken[]
export const DEFAULT_SORT_TOKEN: WorkoutSortToken = 'newest'

export const sortToToken = (sort: WorkoutSort): WorkoutSortToken =>
  WORKOUT_SORT_TOKENS.find(
    (token) =>
      WORKOUT_SORT_BY_TOKEN[token].orderField === sort.orderField &&
      WORKOUT_SORT_BY_TOKEN[token].orderDirection === sort.orderDirection,
  ) ?? DEFAULT_SORT_TOKEN

export const tokenToSort = (token: string | null): WorkoutSort =>
  (token && WORKOUT_SORT_BY_TOKEN[token as WorkoutSortToken]) || DEFAULT_WORKOUT_SORT

/** Decode the workouts query from the URL. Unknown values pass through; page is clamped to ≥ 1. */
export const parseWorkoutQuery = (params: URLSearchParams): WorkoutQueryState => {
  const workoutFilter: WorkoutFilterAttributes = {}
  for (const key of WORKOUT_FILTER_KEYS) {
    const values = params.getAll(key)
    if (values.length) workoutFilter[key] = values
  }
  const pageRaw = Number(params.get(PAGE_PARAM))
  const page = Number.isInteger(pageRaw) && pageRaw >= 1 ? pageRaw : 1
  return {
    workoutFilter,
    sort: tokenToSort(params.get(SORT_PARAM)),
    hideCompleted: params.get(HIDE_COMPLETED_PARAM) === 'true',
    page,
  }
}

/**
 * Encode the workouts query into search params, dropping defaults (Newest First, page 1,
 * hideCompleted=false, empty categories) so a clean state yields a clean URL. Any non-workout params
 * on `base` are preserved.
 */
export const buildWorkoutQuery = (state: WorkoutQueryState, base?: URLSearchParams): URLSearchParams => {
  const params = new URLSearchParams(base ?? undefined)
  for (const key of [...WORKOUT_FILTER_KEYS, SORT_PARAM, HIDE_COMPLETED_PARAM, PAGE_PARAM]) {
    params.delete(key)
  }
  for (const key of WORKOUT_FILTER_KEYS) {
    const values = state.workoutFilter[key]
    if (values && values.length) values.forEach((value) => params.append(key, value))
  }
  const token = sortToToken(state.sort)
  if (token !== DEFAULT_SORT_TOKEN) params.set(SORT_PARAM, token)
  if (state.hideCompleted) params.set(HIDE_COMPLETED_PARAM, 'true')
  if (state.page > 1) params.set(PAGE_PARAM, String(state.page))
  return params
}
